export type NormalizedAddress = {
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  number?: string;
  postalCode?: string;
};

// Simple, universal approach: extract the first valid street address
export const extractPrimaryAddress = (addressString: string): string => {
  if (!addressString || typeof addressString !== 'string') {
    return '';
  }

  const input = addressString.trim();

  // Step 1: Split on common multi-address delimiters
  const delimiters = [' / ', ' - ', ' | ', '; ', '\n', '\r\n'];
  let candidates = [input];

  for (const delimiter of delimiters) {
    if (input.includes(delimiter)) {
      candidates = input
        .split(delimiter)
        .map(s => s.trim())
        .filter(s => s.length > 0);
      break;
    }
  }

  // Step 2: Find the first candidate that looks like a street address
  for (const candidate of candidates) {
    const isValid = isValidStreetAddress(candidate);

    if (isValid) {
      return candidate;
    }
  }

  // Step 3: If no valid street address found, return the first candidate
  const result = candidates[0] || input;

  return result;
};

const isValidStreetAddress = (text: string): boolean => {
  const clean = text.trim();

  if (clean.length < 10 || clean.length > 200) return false;

  if (!/\d/.test(clean) || !/[a-zA-Z]/.test(clean)) return false;

  if (/^(contact|variant|phone|email|tel|fax)[\s:]/i.test(clean)) return false;

  const hasStreetIndicators = [
    /\b(st|street|ave|avenue|blvd|boulevard|rd|road|dr|drive|ln|lane|ct|court|pl|place|way|circle|square)\b/i,
    /\b(ca|ny|tx|fl|vic|nsw|qld|london|paris|berlin|sofia)\b/i,
    /\b\d{4,5}(-\d{4})?\b/,
    /^\d+\s+[a-zA-Z]/,
  ];

  return hasStreetIndicators.some(pattern => pattern.test(clean));
};

export const normalizeAddress = (
  address: string,
  countryCode?: string | null,
): NormalizedAddress => {
  const parts = address.split(',').map(part => part.trim());

  return {
    street: parts[0] || '',
    city: parts[1] || '',
    state: parts[2] || '',
    country: parts[parts.length - 1] || countryCode || '',
    number: '',
    postalCode: '',
  };
};

export const enrichAddressWithGeocodeResults = (
  baseAddress: NormalizedAddress,
  geocodeResult: google.maps.GeocoderResult,
): NormalizedAddress => {
  const components = geocodeResult.address_components;
  const enriched = { ...baseAddress };

  components.forEach(component => {
    const types = component.types;

    if (types.includes('street_number')) {
      enriched.number = component.long_name;
    }

    if (types.includes('route')) {
      enriched.street = component.long_name;
    }

    if (types.includes('locality')) {
      enriched.city = component.long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      enriched.state = component.long_name;
    }

    if (types.includes('country')) {
      enriched.country = component.long_name;
    }

    if (types.includes('postal_code')) {
      enriched.postalCode = component.long_name;
    }
  });

  return enriched;
};
